/* eslint-disable max-lines */
import axios, { AxiosResponse } from "axios";
import { IGTFSBlockedEntity, IGTFSBlockInsert, IGTFSEnrollmentLog, IGTFSLine, IGTFSLineDetailsResponse, IGTFSLineUpsertForm, IGTFSOperator, IGTFSOperatorResponse, IGTFSShape, IGTFSStation, IGTFSTripStop, IGTFSTripStopUpsert, IGTFSUploadResult, IGTFSValidationResult } from "../interfaces/endpoints/IGTFS.types";
import { IAxiosResponse } from "../interfaces/IAxiosResponse";
import { IPagination } from "../interfaces/IPagination";
import { SelectParamsType } from "../interfaces/ISelectParams";
import { serialize } from "../utils/serializeToQueryString";

const BASE_URL = "v1/gtfs";
const GTFS_API = {
  SHAPES: `${BASE_URL}/shapes`,
  STATIONS: `${BASE_URL}/stops`,
  LINES: `${BASE_URL}/lines`,
  OPERATORS: `${BASE_URL}/agencies`,
  GENERATE_FEED: `${BASE_URL}/generate-feed`,
  VALIDATIONS: `${BASE_URL}/validations`,
  ENROLLMENT_LOG: `${BASE_URL}/enrollment-log`,
  GENERATE_SHAPE: `${BASE_URL}/shapes/generate`,
  BLOCKS: `${BASE_URL}/blocks`,
  TRIP_STOPS: `${BASE_URL}/trip-stops`
};

// The GTFS-only added stops: stops the feed publishes between a trip's two ends so that no hop
// reaches 24 hours. 400 and 404 come back as responses so the pages can show the backend's reason.
const passClientErrors = {
  validateStatus: (status: number) => (status >= 200 && status < 300) || status === 404 || status === 400
};

export const getGTFSTripStops = async (params: SelectParamsType): Promise<IAxiosResponse<IPagination<IGTFSTripStop>>> =>
  axios.get(`${GTFS_API.TRIP_STOPS}${serialize(params)}`);

export const getGTFSTripStop = async (id: number): Promise<IAxiosResponse<IGTFSTripStop>> =>
  axios.get(`${GTFS_API.TRIP_STOPS}/${id}`);

export const createGTFSTripStop = async (data: IGTFSTripStopUpsert): Promise<IAxiosResponse<IGTFSTripStop>> =>
  axios.post(GTFS_API.TRIP_STOPS, data, passClientErrors);

export const updateGTFSTripStop = async (id: number, data: IGTFSTripStopUpsert): Promise<IAxiosResponse<IGTFSTripStop>> =>
  axios.put(`${GTFS_API.TRIP_STOPS}/${id}`, data, passClientErrors);

export const deleteGTFSTripStop = async (id: number): Promise<IAxiosResponse<boolean>> =>
  axios.delete(`${GTFS_API.TRIP_STOPS}/${id}`);

export const getAllShapes = async (params: SelectParamsType): Promise<IAxiosResponse<IPagination<IGTFSShape>>> =>
  axios.get(`${GTFS_API.SHAPES}${serialize(params)}`);

export const getShape = (id: number): Promise<AxiosResponse<IGTFSShape>> =>
  axios.get(`${GTFS_API.SHAPES}/${id}`);

export const createShape = (formData: FormData): Promise<AxiosResponse<IGTFSShape>> =>
  axios.post(`${GTFS_API.SHAPES}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data"
    },
    validateStatus: (status) => (status >= 200 && status < 300) || status === 404 || status === 400
  });

export const deleteShape = (id: number): Promise<IAxiosResponse<boolean>> =>
  axios.delete(`${GTFS_API.SHAPES}/${id}`);

export const updateShape = (id: number, formData: FormData): Promise<IAxiosResponse<IGTFSShape>> =>
  axios.put(`${GTFS_API.SHAPES}/${id}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data"
    },
    validateStatus: (status) => (status >= 200 && status < 300) || status === 404 || status === 400
  });

export const updateGTFSStation = async (data: IGTFSStation): Promise<IAxiosResponse<IGTFSStation>> => {
  return axios.put(GTFS_API.STATIONS, data, {
    validateStatus: (status) => (status >= 200 && status < 300) || status === 404 || status === 400
  });
};

export const deleteGTFSStation = async (id: number): Promise<IAxiosResponse<boolean>>  => {
  return axios.delete(`${GTFS_API.STATIONS}/${id}`);
};

export const getGTFSStations = async (params: SelectParamsType): Promise<IAxiosResponse<IPagination<IGTFSStation>>> =>
  axios.get(`${GTFS_API.STATIONS}${serialize(params)}`);

export const getGTFSStation = async (id: number): Promise<IAxiosResponse<IGTFSStation>> =>
  axios.get(`${GTFS_API.STATIONS}/${id}`);

// The same, but a station without a GTFS stop answers as a 404 response instead of a thrown error.
export const getGTFSStationIfAny = async (id: number): Promise<IAxiosResponse<IGTFSStation>> =>
  axios.get(`${GTFS_API.STATIONS}/${id}`, passClientErrors);

// Makes the stop follow its station again for the chosen fields. The query is written out because
// serialize() drops false values, and the backend treats a missing flag as true.
export const resetGTFSStationToStation = async (
  id: number,
  fields: { name: boolean; location: boolean }
): Promise<IAxiosResponse<IGTFSStation>> =>
  axios.post(
    `${GTFS_API.STATIONS}/${id}/reset-to-station?name=${fields.name}&location=${fields.location}`,
    null,
    passClientErrors
  );

export const createGTFSStation = async (data: IGTFSStation): Promise<IAxiosResponse<IGTFSStation>> => {
  return axios.post(GTFS_API.STATIONS, data, {
    validateStatus: (status) => (status >= 200 && status < 300) || status === 404 || status === 400
  });
};

export const getLines = async (params: SelectParamsType): Promise<IAxiosResponse<IPagination<IGTFSLine>>> => {
  return axios.get(`${GTFS_API.LINES}${serialize(params)}`);
};

export const deleteLine = (id: number): Promise<AxiosResponse<boolean>> =>
  axios.delete(`${GTFS_API.LINES}/${id}`);

export const getShapesSelectList = async (params: SelectParamsType): Promise<IAxiosResponse<IPagination<{ id: number; name: string }>>> => {
  return axios.get(`${GTFS_API.SHAPES}${serialize(params)}`);
};

export const generateShapeForLine = async (lineId: number): Promise<AxiosResponse<IGTFSShape>> =>
  axios.post(`${GTFS_API.GENERATE_SHAPE}/${lineId}`, undefined, {
    validateStatus: (status) => (status >= 200 && status < 300) || status === 400
  });

export const getLineDetails = async (lineId: number): Promise<IAxiosResponse<IGTFSLineDetailsResponse>> => {
  return axios.get(`${GTFS_API.LINES}/${lineId}`);
};

export const upsertGTFSLine = (data: IGTFSLineUpsertForm): Promise<IAxiosResponse<IGTFSLine>> =>
  axios.post(`${GTFS_API.LINES}`, data, {
    validateStatus: (status) => (status >= 200 && status < 300) || status === 404 || status === 400
  });

export const getOperators = async (params: SelectParamsType): Promise<IAxiosResponse<IGTFSOperatorResponse>> =>
  axios.get(`${GTFS_API.OPERATORS}${serialize(params)}`);

export const getOperator = async (id: number): Promise<IAxiosResponse<IGTFSOperator>> =>
  axios.get(`${GTFS_API.OPERATORS}/${id}`);

export const updateOperator = async (data: IGTFSOperator): Promise<IAxiosResponse<IGTFSOperator>> => {
  return axios.put(GTFS_API.OPERATORS, data, {
    validateStatus: (status) => (status >= 200 && status < 300) || status === 404 || status === 400
  });
};
export const generateGTFSFeed = async (): Promise<IAxiosResponse<IGTFSUploadResult>> => {
  return axios.get(GTFS_API.GENERATE_FEED, {
    validateStatus: (status) => (status >= 200 && status < 300) || status === 404 || status === 400
  });
};

export const triggerGenerateGTFSFeed = async (): Promise<IAxiosResponse<{ message: string }>> => {
  return axios.post(GTFS_API.GENERATE_FEED, undefined, {
    validateStatus: (status) =>
      (status >= 200 && status < 300) || status === 202 || status === 404 || status === 400
  });
};

export const getValidations = async (params: SelectParamsType): Promise<IAxiosResponse<IPagination<IGTFSValidationResult>>> => {
  return axios.get(`${GTFS_API.VALIDATIONS}${serialize(params)}`);
};

export const getValidation = async (id: number): Promise<IAxiosResponse<IGTFSValidationResult>> =>
  axios.get(`${GTFS_API.VALIDATIONS}/${id}`);

export const getEnrollmentLog = async (params: SelectParamsType): Promise<IAxiosResponse<IPagination<IGTFSEnrollmentLog>>> =>
  axios.get(`${GTFS_API.ENROLLMENT_LOG}${serialize(params)}`);

export const getShapePoints = (id: number): Promise<AxiosResponse<{
  name: string; points: { lat: number; lng: number }[];
}>> => axios.get(`${GTFS_API.SHAPES}/${id}/points`);

export const getGTFSBlocks = async (params: SelectParamsType): Promise<IAxiosResponse<IPagination<IGTFSBlockedEntity>>> =>
  axios.get(`${GTFS_API.BLOCKS}${serialize(params)}`);

export const addGTFSBlock = (data: IGTFSBlockInsert): Promise<IAxiosResponse<IGTFSBlockedEntity>> =>
  axios.post(GTFS_API.BLOCKS, data, {
    validateStatus: (status) => (status >= 200 && status < 300) || status === 404 || status === 400
  });

export const deleteGTFSBlock = (id: number): Promise<AxiosResponse<boolean>> =>
  axios.delete(`${GTFS_API.BLOCKS}/${id}`);
